import { useMemo, useState } from 'react';
import { CompanySelector, HeaderIcons, ProfileButton, Logo } from '../header-ui';
import { clearAuthLocalStorage } from '@/features/xac-thuc/actions/log-out';
import { filterMenusByPermissions } from '@/lib/permissions';
import { AritoNavMenu, NavMenu } from '../nav-menu';
import { useAuth } from '@/contexts/auth-context';
import { menus } from '@/constants/navigation';
import { MobileNavMenu } from '../mobile-menu';
import { ProfileMenu } from '../profile-menu';
import { useAppRouter } from '@/routing';

export function AritoAppBar() {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const router = useAppRouter();
  const { isAdmin, permissions } = useAuth();

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsProfileOpen(true);
  };

  const handleProfileClose = () => {
    setAnchorEl(null);
    setIsProfileOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Client-side logout handler
  const handleLogout = () => {
    handleProfileClose(); // Close the menu first
    clearAuthLocalStorage();
    router.push('/xac-thuc');
  };

  const filteredMenus = useMemo<NavMenu[]>(() => {
    return filterMenusByPermissions(menus, permissions, isAdmin);
  }, [isAdmin, permissions]);

  // Mobile menu content
  const mobileMenuContent = (
    <div className='fixed bottom-0 left-0 right-0 top-[var(--erp-mobile-header-height)] z-50 flex flex-col bg-main'>
      <div className='flex-1 overflow-y-auto'>
        {filteredMenus.map((menu: NavMenu, index: number) => (
          <MobileNavMenu
            key={menu.title}
            menu={menu}
            isLast={index === filteredMenus.length - 1}
            isOpen={expandedMenu === menu.title}
            onToggle={title => {
              if (title === expandedMenu) {
                setExpandedMenu(null);
              } else {
                setExpandedMenu(title);
              }
            }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <header className='relative z-40 flex h-fit w-screen flex-col overflow-hidden border-b border-main-foreground/10 bg-main text-main-foreground shadow-sm'>
      {/* Background Settings Gear Rotating Slowly behind profile widget */}
      <div className='pointer-events-none absolute right-2 top-[-10px] z-0 h-[140px] w-[140px] select-none overflow-hidden opacity-[0.06]'>
        <svg
          className='h-full w-full animate-[spin_25s_linear_infinite] text-white'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='0.9'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            d='M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z'
          />
          <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
        </svg>
      </div>

      {/* Desktop view */}
      <div className='relative z-10 hidden lg:block'>
        {/* Row 1: Logo on the left, User controls on the right */}
        <div className='flex h-[var(--erp-appbar-user-row-height)] items-center justify-between gap-3 px-4 py-1'>
          <Logo />
          <div className='flex items-center gap-3'>
            <CompanySelector />
            <HeaderIcons />
            <ProfileButton onClick={handleProfileClick} />
          </div>
        </div>

        <div className='h-px bg-main-foreground/15' />

        {/* Row 2: Category navigation spanning the full width */}
        <nav className='flex max-h-[calc((var(--erp-appbar-nav-row-height)*var(--erp-appbar-nav-max-rows))+(var(--erp-appbar-nav-row-gap)*(var(--erp-appbar-nav-max-rows)-1))+(var(--erp-appbar-nav-padding-y)*2))] min-h-[calc(var(--erp-appbar-nav-row-height)+(var(--erp-appbar-nav-padding-y)*2))] flex-wrap content-center items-center gap-x-0.5 gap-y-[var(--erp-appbar-nav-row-gap)] overflow-hidden px-4 py-[var(--erp-appbar-nav-padding-y)]'>
          {filteredMenus.map((menu: NavMenu) => (
            <AritoNavMenu key={menu.title} title={menu.title} items={menu.items} />
          ))}
        </nav>
      </div>

      {/* Mobile view */}
      <div className='relative z-10 flex h-[var(--erp-mobile-header-height)] w-full items-center justify-between bg-main px-4 shadow-sm lg:hidden'>
        <button
          onClick={toggleMobileMenu}
          className='flex h-[var(--erp-header-icon-size)] w-[var(--erp-header-icon-size)] items-center justify-center rounded-full text-main-foreground transition-colors hover:bg-main-foreground/10 focus:outline-none'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            className='h-5 w-5'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h16' />
          </svg>
        </button>

        <Logo isMobile={true} />

        {/* Mobile header icons */}
        <div className='flex items-center gap-2'>
          <CompanySelector isMobile={true} />
          <HeaderIcons isMobile={true} />
          <ProfileButton isMobile={true} onClick={handleProfileClick} />
        </div>

        {/* Render the menu content when mobile menu is open */}
        {isMobileMenuOpen && mobileMenuContent}
      </div>

      {/* Profile dropdown menu */}
      {isProfileOpen && (
        <ProfileMenu anchorEl={anchorEl} isOpen={isProfileOpen} onClose={handleProfileClose} onLogout={handleLogout} />
      )}
    </header>
  );
}
